import styled from '@emotion/styled';
import { settingsTheme as t } from '../../theme';
import { Instructor, InstructorStatus, InstructorType } from '../data/instructors';
import TypeBadge from './TypeBadge';
import StatusIndicator from './StatusIndicator';
import RowActions from './RowActions';
import EmptyState from './EmptyState';

interface ColumnLabels {
  instructor: string;
  organization: string;
  type: string;
  status: string;
}

interface InstructorsTableProps {
  rows: Instructor[];
  /** Muestra las acciones de editar/eliminar por fila solo si hay permiso. */
  canManage?: boolean;
  columnLabels: ColumnLabels;
  typeLabel: (type: InstructorType) => string;
  statusLabel: (status: InstructorStatus) => string;
  editLabel: string;
  deleteLabel: string;
  emptyMessage: string;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const GRID = '2.3fr 1.7fr 1fr 1.1fr 86px';

const Surface = styled.div({
  marginTop: 20,
  background: t.color.surface,
  border: `1px solid ${t.color.border}`,
  borderRadius: t.radius.lg,
  overflow: 'hidden',
  boxShadow: t.shadow.card,
});

const HeadRow = styled.div({
  display: 'grid',
  gridTemplateColumns: GRID,
  gap: 16,
  padding: '14px 24px',
  background: t.color.tableHeadBg,
  borderBottom: `1px solid ${t.color.rowBorder}`,
});

const HeadCell = styled.span({
  fontFamily: t.font,
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: '0.6px',
  color: t.color.tableHeadText,
});

const BodyRow = styled.div({
  display: 'grid',
  gridTemplateColumns: GRID,
  alignItems: 'center',
  gap: 16,
  padding: '17px 24px',
  borderTop: `1px solid ${t.color.rowBorder}`,
  '&:hover': { background: t.color.rowHoverBg },
});

const Identity = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 13,
  minWidth: 0,
});

const IdentityText = styled.div({ minWidth: 0 });

const Name = styled.div({
  fontFamily: t.font,
  fontSize: 14.5,
  fontWeight: 600,
  color: t.color.title,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Email = styled.div({
  fontFamily: t.font,
  fontSize: 12.5,
  color: t.color.muted,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Organization = styled.div({
  fontFamily: t.font,
  fontSize: 13.5,
  color: t.color.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

/**
 * Organismo: tabla de instructores. Ensambla los primitivos de fila
 * (nombre/correo, tipo, estatus y acciones) en cada renglón y delega los eventos.
 * Si no hay filas, muestra el estado vacío.
 */
const InstructorsTable = ({
  rows,
  canManage = true,
  columnLabels,
  typeLabel,
  statusLabel,
  editLabel,
  deleteLabel,
  emptyMessage,
  onEdit,
  onDelete,
}: InstructorsTableProps) => (
  <Surface>
    <HeadRow>
      <HeadCell>{columnLabels.instructor}</HeadCell>
      <HeadCell>{columnLabels.organization}</HeadCell>
      <HeadCell>{columnLabels.type}</HeadCell>
      <HeadCell>{columnLabels.status}</HeadCell>
      <span />
    </HeadRow>

    {rows.map(row => (
      <BodyRow key={row.id}>
        <Identity>
          <IdentityText>
            <Name>{row.name}</Name>
            <Email>{row.email}</Email>
          </IdentityText>
        </Identity>
        <Organization>{row.organization.trim() ? row.organization : '—'}</Organization>
        <div>
          <TypeBadge type={row.type} label={typeLabel(row.type)} />
        </div>
        <StatusIndicator status={row.status} label={statusLabel(row.status)} />
        {canManage ? (
          <RowActions
            onEdit={() => onEdit(row.id)}
            onDelete={() => onDelete(row.id)}
            editLabel={editLabel}
            deleteLabel={deleteLabel}
          />
        ) : (
          <span />
        )}
      </BodyRow>
    ))}

    {rows.length === 0 ? <EmptyState message={emptyMessage} /> : null}
  </Surface>
);

export default InstructorsTable;
